import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Search, Menu, X, User, PenLine, LogOut, ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import EspressoLogo from "./EspressoLogo";
import RioLogo from "./RioLogo";
import GoogleAnalytics from "./GoogleAnalytics";
import useAnalytics from "../../hooks/useAnalytics";
import { useProject } from "../../context/ProjectContext";
import { themeFromHost } from "../../lib/themeFromHost";

const categories = [
  { slug: "business", label: "Business" },
  { slug: "politics", label: "Politics" },
  { slug: "technology", label: "Technology" },
  { slug: "entertainment", label: "Entertainment" },
  { slug: "sports", label: "Sports" },
  { slug: "health", label: "Health" },
];

/* ============================================================
 * ESPRESSO MORNING — warm editorial header
 * ============================================================ */
const EspressoHeader = ({ scrolled, onSearch, searchQuery, setSearchQuery, mobileMenuOpen, setMobileMenuOpen, authBlock }) => (
  <header
    className={`sticky top-0 z-50 transition-shadow duration-300 ${
      scrolled ? "shadow-[0_8px_24px_-16px_rgba(42,29,24,0.18)]" : ""
    }`}
    style={{
      backgroundColor: "rgba(253,251,247,0.85)",
      backdropFilter: "blur(20px)",
      WebkitBackdropFilter: "blur(20px)",
      borderBottom: "1px solid var(--em-border)",
    }}
    data-testid="news-header"
  >
    <div className="max-w-7xl mx-auto px-6 lg:px-8">
      <div className="h-20 flex items-center justify-between gap-6">
        <Link to="/" data-testid="site-logo" className="shrink-0">
          <EspressoLogo />
        </Link>

        <form onSubmit={onSearch} className="hidden md:flex items-center flex-1 max-w-sm">
          <div className="relative w-full">
            <Input
              type="text"
              placeholder="Search the morning brief…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 bg-transparent rounded-full"
              style={{ border: "1px solid var(--em-border)", color: "var(--em-text)" }}
              data-testid="search-input"
            />
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: "var(--em-text-muted)" }} />
          </div>
        </form>

        <div className="hidden md:flex items-center gap-2">{authBlock}</div>

        <Button
          variant="ghost"
          size="icon"
          className="md:hidden"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          data-testid="mobile-menu-toggle"
        >
          {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </Button>
      </div>

      <nav
        className="hidden md:flex items-center justify-center gap-1 py-3"
        style={{ borderTop: "1px solid var(--em-border)" }}
        aria-label="Primary"
      >
        {categories.map((cat) => (
          <Link
            key={cat.slug}
            to={`/category/${cat.slug}`}
            className="px-4 py-2 text-sm font-medium rounded-full transition-colors"
            style={{ color: "var(--em-text-muted)" }}
            onMouseEnter={(e) => (e.currentTarget.style.color = "var(--em-accent)")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "var(--em-text-muted)")}
            data-testid={`category-${cat.slug}`}
          >
            {cat.label}
          </Link>
        ))}
      </nav>
    </div>
  </header>
);

/* ============================================================
 * NEWS RIO — brutalist breaking-news header with marquee + live dot
 * ============================================================ */
const RioHeader = ({ onSearch, searchQuery, setSearchQuery, mobileMenuOpen, setMobileMenuOpen, authBlock }) => {
  const tickerItems = [
    "BREAKING — MARKETS OPEN MIXED",
    "ASIA HITS RECORD HIGH",
    "TECH STOCKS RALLY +3.2%",
    "EU FINALIZES AI ACT",
    "OIL HOLDS NEAR 78 USD",
    "FED SIGNALS RATE PAUSE",
  ];
  return (
    <header className="sticky top-0 z-50" data-testid="news-header">
      <div style={{ height: "2px", backgroundColor: "var(--rio-accent)" }} />
      {/* breaking news ticker */}
      <div
        className="overflow-hidden font-mono text-xs uppercase py-1"
        style={{ backgroundColor: "#000", borderBottom: "1px solid var(--rio-border)" }}
      >
        <div className="rio-marquee-track" style={{ color: "var(--rio-text-muted)" }}>
          {[...tickerItems, ...tickerItems].map((t, i) => (
            <span key={i} className="inline-flex items-center gap-2">
              <span className="rio-live-dot" />
              {t}
            </span>
          ))}
        </div>
      </div>

      <div style={{ backgroundColor: "var(--rio-bg)", borderBottom: "1px solid var(--rio-border)" }}>
        <div className="max-w-7xl mx-auto px-4 lg:px-6">
          <div className="h-16 flex items-center justify-between gap-4">
            <Link to="/" data-testid="site-logo" className="shrink-0">
              <RioLogo />
            </Link>
            <form onSubmit={onSearch} className="hidden md:flex flex-1 max-w-sm">
              <div className="relative w-full">
                <Input
                  type="text"
                  placeholder="SEARCH"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 rounded-none uppercase"
                  style={{
                    backgroundColor: "var(--rio-surface)",
                    border: "1px solid var(--rio-border)",
                    color: "var(--rio-text)",
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: "0.8rem",
                  }}
                  data-testid="search-input"
                />
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: "var(--rio-text-muted)" }} />
              </div>
            </form>
            <div className="hidden md:flex items-center gap-2">{authBlock}</div>
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              style={{ color: "var(--rio-text)" }}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>

          <nav
            className="hidden md:flex items-center justify-start gap-0 overflow-x-auto"
            style={{ borderTop: "1px solid var(--rio-border)" }}
            aria-label="Primary"
          >
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="px-5 py-3 text-xs uppercase tracking-widest font-bold transition-all"
                style={{
                  color: "var(--rio-text-muted)",
                  borderRight: "1px solid var(--rio-border)",
                  fontFamily: "'JetBrains Mono', monospace",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "var(--rio-accent)";
                  e.currentTarget.style.color = "#000";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                  e.currentTarget.style.color = "var(--rio-text-muted)";
                }}
                data-testid={`category-${cat.slug}`}
              >
                {cat.label}
              </Link>
            ))}
          </nav>
        </div>
      </div>
    </header>
  );
};

/* ============================================================
 * BUSINESS (fallback) — original layout kept minimal
 * ============================================================ */
const BusinessHeader = ({ scrolled, onSearch, searchQuery, setSearchQuery, mobileMenuOpen, setMobileMenuOpen, authBlock, siteName }) => (
  <header className={`sticky top-0 z-50 transition-all duration-200 bg-[#0F172A] ${scrolled ? "shadow-md" : ""}`} data-testid="news-header">
    <div className="max-w-7xl mx-auto px-4">
      <div className="h-16 flex items-center justify-between text-white">
        <Link to="/" className="text-2xl font-bold" data-testid="site-logo">
          {siteName || "News"}
        </Link>
        <form onSubmit={onSearch} className="hidden md:flex flex-1 max-w-md mx-8">
          <div className="relative w-full">
            <Input
              type="text"
              placeholder="Search articles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/50"
              data-testid="search-input"
            />
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/50" />
          </div>
        </form>
        <div className="hidden md:flex items-center gap-2">{authBlock}</div>
        <Button variant="ghost" size="icon" className="md:hidden text-white" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-toggle">
          {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </Button>
      </div>
      <nav className="hidden md:flex items-center gap-1 py-2 border-t border-white/10">
        {categories.map((cat) => (
          <Link
            key={cat.slug}
            to={`/category/${cat.slug}`}
            className="px-4 py-2 text-sm font-medium rounded-full transition-colors text-white/80 hover:text-white hover:bg-white/10"
            data-testid={`category-${cat.slug}`}
          >
            {cat.label}
          </Link>
        ))}
      </nav>
    </div>
  </header>
);

/* ============================================================
 * NewsLayout — orchestrator
 * ============================================================ */
const NewsLayout = ({ children, theme, siteName }) => {
  const { user, isAuthenticated, isWriter, logout } = useAuth();
  const { publicProject } = useProject();
  const resolvedTheme = theme || publicProject?.theme || themeFromHost();
  const resolvedSiteName = siteName || publicProject?.name;
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [scrolled, setScrolled] = useState(false);

  // Auto-track every public page view (analytics dashboard reads from here)
  useAnalytics(publicProject?.id);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
      setSearchQuery("");
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const isEspresso = resolvedTheme === "espresso";
  const isRio = resolvedTheme === "rio";

  /* shared auth block — visually adapts via inline style */
  const authBlock = isAuthenticated ? (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="gap-2" data-testid="user-menu-trigger">
          <User className="h-4 w-4" />
          {user?.name}
          <ChevronDown className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        {isWriter && (
          <DropdownMenuItem onClick={() => navigate("/writer")} data-testid="writer-dashboard-link">
            <PenLine className="h-4 w-4 mr-2" />
            Writer Dashboard
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleLogout} data-testid="logout-menu-item">
          <LogOut className="h-4 w-4 mr-2" />
          Logout
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  ) : (
    <>
      <Button variant="ghost" onClick={() => navigate("/auth")} data-testid="login-button">
        Sign In
      </Button>
      <Button
        onClick={() => navigate("/become-writer")}
        className="rounded-full"
        style={
          isEspresso
            ? { backgroundColor: "var(--em-accent)", color: "#FDFBF7" }
            : isRio
            ? { backgroundColor: "var(--rio-accent)", color: "#000", borderRadius: 0, fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.1em", textTransform: "uppercase", fontWeight: 700 }
            : undefined
        }
        data-testid="become-writer-button"
      >
        {isRio ? "WRITE" : "Become a Writer"}
      </Button>
    </>
  );

  const containerStyle = isEspresso
    ? { backgroundColor: "var(--em-bg)", color: "var(--em-text)" }
    : isRio
    ? { backgroundColor: "var(--rio-bg)", color: "var(--rio-text)" }
    : undefined;

  return (
    <div data-theme={isEspresso ? "espresso" : isRio ? "rio" : "business"} style={containerStyle} className="min-h-screen" data-testid="news-layout">
      <GoogleAnalytics gaId={publicProject?.google_analytics_id} gtmId={publicProject?.google_tag_manager_id} />
      {isEspresso && (
        <EspressoHeader
          scrolled={scrolled}
          onSearch={handleSearch}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          mobileMenuOpen={mobileMenuOpen}
          setMobileMenuOpen={setMobileMenuOpen}
          authBlock={authBlock}
        />
      )}
      {isRio && (
        <RioHeader
          onSearch={handleSearch}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          mobileMenuOpen={mobileMenuOpen}
          setMobileMenuOpen={setMobileMenuOpen}
          authBlock={authBlock}
        />
      )}
      {!isEspresso && !isRio && (
        <BusinessHeader
          scrolled={scrolled}
          onSearch={handleSearch}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          mobileMenuOpen={mobileMenuOpen}
          setMobileMenuOpen={setMobileMenuOpen}
          authBlock={authBlock}
          siteName={resolvedSiteName}
        />
      )}

      {/* Mobile menu — shared */}
      {mobileMenuOpen && (
        <div
          className="md:hidden border-t"
          style={
            isEspresso
              ? { backgroundColor: "var(--em-bg)", borderColor: "var(--em-border)" }
              : isRio
              ? { backgroundColor: "var(--rio-bg)", borderColor: "var(--rio-border)" }
              : { backgroundColor: "#0F172A", borderColor: "rgba(255,255,255,0.1)" }
          }
        >
          <div className="p-4 space-y-2">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                onClick={() => setMobileMenuOpen(false)}
                className="block px-4 py-3 rounded-lg"
              >
                {cat.label}
              </Link>
            ))}
          </div>
        </div>
      )}

      <main id="main" className="min-h-[calc(100vh-220px)]">
        {children}
      </main>

      <Footer theme={resolvedTheme} siteName={resolvedSiteName} />
    </div>
  );
};

/* ============================================================
 * Footer — theme-aware
 * ============================================================ */
const Footer = ({ theme, siteName }) => {
  const year = new Date().getFullYear();
  const name = siteName || (theme === "espresso" ? "Espresso Morning" : theme === "rio" ? "News Rio" : "News");

  if (theme === "espresso") {
    return (
      <footer
        className="mt-24"
        style={{ backgroundColor: "#2A1D18", color: "#FDFBF7" }}
        data-testid="news-footer"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <h3 style={{ fontFamily: "'Playfair Display', serif" }} className="text-3xl font-bold mb-4">
              {name}
            </h3>
            <p className="text-sm leading-relaxed opacity-70 max-w-md">
              Slow journalism for fast mornings. Curated, thoughtful, and delivered with the precision of a perfect pour.
            </p>
          </div>
          <div className="md:col-span-3">
            <h4 className="text-xs uppercase tracking-[0.25em] opacity-60 mb-4">Sections</h4>
            <ul className="space-y-2">
              {categories.slice(0, 4).map((c) => (
                <li key={c.slug}>
                  <Link to={`/category/${c.slug}`} className="hover:underline opacity-90">
                    {c.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div className="md:col-span-4">
            <h4 className="text-xs uppercase tracking-[0.25em] opacity-60 mb-4">House</h4>
            <ul className="space-y-2 opacity-90">
              <li><Link to="/become-writer" className="hover:underline">Write for us</Link></li>
              <li><a href={`mailto:hello@${(typeof window!=='undefined'?window.location.host:'espressomorning.news')}`} className="hover:underline">Contact</a></li>
              <li><Link to="/privacy" className="hover:underline">Privacy</Link></li>
              <li><Link to="/terms" className="hover:underline">Terms</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/10 py-6 text-center text-xs opacity-50">
          © {year} {name}. Brewed with care.
        </div>
      </footer>
    );
  }

  if (theme === "rio") {
    return (
      <footer style={{ backgroundColor: "var(--rio-bg)", borderTop: "1px solid var(--rio-border)", color: "var(--rio-text)" }} data-testid="news-footer">
        <div className="overflow-hidden py-3" style={{ borderBottom: "1px solid var(--rio-border)" }}>
          <div className="rio-marquee-track" style={{ fontFamily: "'Anton', sans-serif", fontSize: "4rem", lineHeight: 1, color: "var(--rio-surface)", WebkitTextStroke: "1px var(--rio-text-muted)" }}>
            {Array.from({ length: 12 }).map((_, i) => (
              <span key={i}>NEWS RIO · NEWS RIO · NEWS RIO · </span>
            ))}
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 lg:px-6 py-12 grid grid-cols-2 md:grid-cols-4 gap-8 text-sm" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
          <div className="col-span-2">
            <RioLogo />
            <p className="mt-4 text-xs uppercase tracking-widest" style={{ color: "var(--rio-text-muted)" }}>
              Breaking news. Every minute. Unfiltered.
            </p>
          </div>
          <div>
            <h4 className="uppercase text-xs mb-3" style={{ color: "var(--rio-accent)" }}>Sections</h4>
            <ul className="space-y-2">
              {categories.slice(0, 4).map((c) => (
                <li key={c.slug}>
                  <Link to={`/category/${c.slug}`} className="uppercase hover:text-white" style={{ color: "var(--rio-text-muted)" }}>
                    &gt; {c.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="uppercase text-xs mb-3" style={{ color: "var(--rio-accent)" }}>Desk</h4>
            <ul className="space-y-2">
              <li><Link to="/become-writer" className="uppercase hover:text-white" style={{ color: "var(--rio-text-muted)" }}>&gt; Write</Link></li>
              <li><Link to="/privacy" className="uppercase hover:text-white" style={{ color: "var(--rio-text-muted)" }}>&gt; Privacy</Link></li>
              <li><Link to="/terms" className="uppercase hover:text-white" style={{ color: "var(--rio-text-muted)" }}>&gt; Terms</Link></li>
            </ul>
          </div>
        </div>
        <div className="text-center text-xs py-4 uppercase tracking-widest" style={{ color: "var(--rio-text-muted)", borderTop: "1px solid var(--rio-border)", fontFamily: "'JetBrains Mono', monospace" }}>
          © {year} NEWS RIO // RAW NEWS, RIGHT NOW
        </div>
      </footer>
    );
  }

  // fallback
  return (
    <footer className="mt-16 bg-[#0F172A] text-white" data-testid="news-footer">
      <div className="max-w-7xl mx-auto px-4 py-12 text-center text-sm opacity-60">
        © {year} {name}.
      </div>
    </footer>
  );
};

export default NewsLayout;
